// React
import React from 'react';

// Packages
import t from 'prop-types';
import cc from 'classcat';

// Utilities
import { useLabels, useTagging, useFormat } from 'utilities/hooks';

// Components
import Card from 'components/_cardContentElements';
import Tags from 'components/tags';

const ActivityDisseminationComponent = ({
    item,
    theme = 'teal',
    CONSTANTS,
    wrap = false,
}) => {
    // ///////////////////
    // HOOKS
    // ///////////////////

    const format = useFormat();
    const { dataSet, getValueLabel, object, label } = useLabels();
    const tagging = useTagging({
        relationKey: 'Initiative_Activity__c',
    });

    // ///////////////////
    // DATA
    // ///////////////////

    const { Activity_Start_Date__c, Activity_End_Date__c } = item;

    const country = item?.Initiative_Location__c
        ? dataSet('Countries').find(
              c => c.value === item?.Initiative_Location__c
          )?.label ?? null
        : null;

    const otherLocation = item?.Additional_Location_Information__c;

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <Card.CardContentWrapper
            {...{
                wrap,
            }}>
            {/* Method */}
            <Card.CardContentSection
                {...{
                    theme,
                    wrap,
                    title: object.label(
                        'Initiative_Activity__c.Dissemination_Method__c'
                    ),
                }}>
                <p>
                    {item?.Dissemination_Method__c?.split(';')
                        .map(x =>
                            getValueLabel(
                                'Initiative_Activity__c.Dissemination_Method__c',
                                x
                            )
                        )
                        .join(', ')}
                </p>
            </Card.CardContentSection>

            {/* Description */}
            <Card.CardContentSection
                {...{
                    theme,
                    wrap,
                }}>
                <p>{item?.Things_To_Do_Description__c}</p>
            </Card.CardContentSection>

            {/* Start date / End date  */}
            {Activity_Start_Date__c && (
                <Card.CardContentSection
                    {...{
                        theme,
                        wrap,
                        title: object.label(
                            'Initiative_Activity__c.Dates_Label'
                        ),
                    }}>
                    <p>
                        {format.date(Activity_Start_Date__c)}
                        {Activity_End_Date__c
                            ? ` - ${format.date(Activity_End_Date__c)}`
                            : ''}
                    </p>
                </Card.CardContentSection>
            )}

            {/* Location */}
            {[country, otherLocation].some(x => x) && (
                <Card.CardContentSection
                    {...{
                        theme,
                        wrap,
                        title: object.label(
                            'Initiative_Activity__c.Initiative_Location__c'
                        ),
                    }}>
                    <p>
                        {otherLocation && otherLocation}
                        {otherLocation && country && `, `}
                        {country && country}
                    </p>
                </Card.CardContentSection>
            )}

            {/* Tags */}
            {tagging?.getCurrentTags(item).length > 0 && (
                <Card.CardContentSection
                    {...{
                        theme,
                        wrap,
                        title: label('DetailsTagsActivities'),
                    }}>
                    <Tags
                        {...{
                            theme,
                            tags: tagging?.getCurrentTags(item),
                            className: 'mt-4',
                        }}
                    />
                </Card.CardContentSection>
            )}

            {/* Journal type */}
            {item?.Dissemination_Method__c ===
                CONSTANTS.ACTIVITIES.ACTIVITY_JOURNAL && (
                <Card.CardContentPairBox>
                    {item?.Publication_Title__c && (
                        <Card.CardContentPair
                            {...{
                                theme,
                                label: object.label(
                                    'Initiative_Activity__c.Publication_Title__c'
                                ),
                                value: item?.Publication_Title__c,
                            }}
                        />
                    )}

                    {item?.Publication_Type__c && (
                        <Card.CardContentPair
                            {...{
                                theme,
                                label: object.label(
                                    'Initiative_Activity__c.Publication_Type__c'
                                ),
                                value: item?.Publication_Type__c,
                            }}
                        />
                    )}

                    {item?.Publication_Year__c && (
                        <Card.CardContentPair
                            {...{
                                theme,
                                label: object.label(
                                    'Initiative_Activity__c.Publication_Year__c'
                                ),
                                value: item?.Publication_Year__c,
                            }}
                        />
                    )}

                    {item?.Publication_Publisher__c && (
                        <Card.CardContentPair
                            {...{
                                theme,
                                label: object.label(
                                    'Initiative_Activity__c.Publication_Publisher__c'
                                ),
                                value: item?.Publication_Publisher__c,
                            }}
                        />
                    )}

                    {item?.Publication_Author__c && (
                        <Card.CardContentPair
                            {...{
                                theme,
                                label: object.label(
                                    'Initiative_Activity__c.Publication_Author__c'
                                ),
                                value: item?.Publication_Author__c,
                            }}
                        />
                    )}

                    {item?.Publication_DOI__c && (
                        <Card.CardContentPair
                            {...{
                                theme,
                                label: object.label(
                                    'Initiative_Activity__c.Publication_DOI__c'
                                ),
                                value: item?.Publication_DOI__c,
                            }}
                        />
                    )}
                </Card.CardContentPairBox>
            )}
        </Card.CardContentWrapper>
    );
};

ActivityDisseminationComponent.propTypes = {
    description: t.string,
    location: t.string,
};

ActivityDisseminationComponent.defaultProps = {};

export default ActivityDisseminationComponent;
